import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { clearActiveProjectStreams } from "@/components/project/AssistantPanel";

export const AuthContext = createContext(null);

// Acceso de propietario único (Fase 2): no hay registro abierto. `setupRequired`
// indica si todavía no existe el propietario (se debe mostrar /setup); si ya
// existe, cualquier visitante sin sesión ve /login. El backend es la única
// fuente de verdad: esto solo refleja su estado.
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [setupRequired, setSetupRequired] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    setAuthLoading(true);
    try {
      const status = await api.setupStatus();
      setSetupRequired(!status.setup_complete);
      if (status.setup_complete) {
        try {
          const me = await api.me();
          setUser(me);
        } catch {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch {
      // Backend inalcanzable: se trata como "no autenticado" en vez de bloquear la app.
      setSetupRequired(false);
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const setup = async (payload) => {
    const me = await api.setup(payload);
    setSetupRequired(false);
    setUser(me);
    return me;
  };

  const reclaimOwner = async (payload) => {
    const me = await api.reclaimOwner(payload);
    setUser(me);
    return me;
  };

  const login = async (payload) => {
    const me = await api.login(payload);
    setUser(me);
    return me;
  };

  const logout = async () => {
    try { clearActiveProjectStreams(); } catch { /* no-op */ }
    try { await api.logout(); } catch { /* no-op: igualmente limpiamos el estado local */ }
    try {
      if (typeof window !== "undefined") {
        if (window.sessionStorage) window.sessionStorage.clear();
        try { globalThis.localStorage?.removeItem("veyra.studio.v1"); } catch { /* no-op */ }
      }
    } catch { /* no-op */ }
    setUser(null);
  };

  const isOwner = Boolean(user && (user.role === "owner" || user.id === "owner" || user._id === "owner"));

  return (
    <AuthContext.Provider value={{ user, isOwner, setUser, bootstrap, setupRequired, authLoading, setup, reclaimOwner, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const DEFAULT_AUTH_FALLBACK = {
  user: { id: "owner", _id: "owner", role: "owner", status: "active" },
  isOwner: true,
  authLoading: false,
  setupRequired: false,
  bootstrap: async () => {},
  login: async () => {},
  logout: async () => {},
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) return DEFAULT_AUTH_FALLBACK;
  return ctx;
};
